import { Chip, IconButton, Stack, Tooltip } from "@mui/material";
import { FaCheck, FaClipboardCheck, FaIndustry, FaMagnifyingGlass } from "react-icons/fa6";
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import ListingPage from '../../components/DynamicListingPage';
import { useQuery } from '@tanstack/react-query';
import { usePermissions } from "../../providers/PermissionsProvider";
import { getProduction } from "../../helpers/http_production_requests";
import { getProductionSteps } from "../../helpers/http_production_steps_requests";
import { stepNames } from "../../utils/data_variables";

export default function Production() {

    const { step } = useParams();

    const { data: stepsResponse } = useQuery({
        queryKey: ['getProductionSteps'],
        queryFn: getProductionSteps,
        staleTime: 1000 * 60,
    });
    const productionSteps = Array.isArray(stepsResponse?.data?.data) ? stepsResponse.data.data : [];

    const titlesByNumber = {
        ...stepNames,
        8: "Pronto",
        ...Object.fromEntries(productionSteps.map((item) => [item.id, item.name])),
    };

    const colorByNumber = {
        0: "info",
        1: "warning",
        2: "warning",
        3: "warning",
        4: "warning",
        5: "warning",
        6: "warning",
        7: "success",
        8: "success"
    }

    const titlesByString = {
        triagem: "Triagem",
        fundicao: "Fundição",
        usinagem: "Usinagem",
        montagem: "Montagem",
        polimento: "Polimento",
        banho: "Banho",
        laser: "Laser",
        fechamento: "Fechamento",
        pronto: "Pronto",
        ...Object.fromEntries(productionSteps.map((item) => [item.slug, item.name])),
    };

    const dinamicPageTitle = titlesByString[step] ? titlesByString[step] : "Produção";

    const { verifyPermission } = usePermissions();
    const [filterValues, setFilterValues] = useState({});

    const { data, isFetching } = useQuery({
        queryKey: ['getProduction', step, filterValues],
        queryFn: () => getProduction(step, filterValues),
    });

    const { data: dataList } = data?.data ?? {};

    const componentName = 'production';

    const columns = [
        { field: 'id', headerName: 'ID' },
        { field: 'image', headerName: 'Foto' },
        {
            field: 'name',
            headerName: 'Nome',
        },
        {
            field: 'step',
            headerName: 'Etapa',
        },
        {
            field: 'category_name',
            headerName: 'Categoria',
        },
        {
            field: 'customer_name',
            headerName: 'Cliente',
        },
        {
            field: 'selected_metal',
            headerName: 'Material',
        },
        {
            field: 'ordered_at',
            headerName: 'Data de Pedido',
        },
        {
            field: 'production_date',
            headerName: 'Data de Produção',
        },
        {
            field: 'delivery_date',
            headerName: 'Data de Entrega',
        },
        {
            field: 'actions',
            headerName: 'Ações',
        },
    ];

    const filters = [
        {
            field: 'overdue',
            type: 'select',
            label: 'Prazo',
            options: [
                { value: '1', label: 'Atrasados' },
                { value: '0', label: 'No prazo' },
            ],
        },
    ];

    function renderStepComponent(param) {
        const { step } = param;

        return <Chip color={colorByNumber[step]} variant={step === "8" || step === 8 ? "filled" : "outlined"} label={titlesByNumber[step]} />;
    };

    const rowStep = (row) => Number(row.step);
    const isTriagemView = step === 'triagem';
    const isFechamentoView = step === 'fechamento';

    const renderActions = (row) => (
        <Stack spacing={2} direction={'row'} alignItems={'center'} justifyContent={'center'}>
            {(isTriagemView || (!step && rowStep(row) === 0)) && (
                <Tooltip title="Triagem" arrow placement="top">
                    <IconButton color="primary" LinkComponent={Link} to={`/producao-triagem/${row.id}`}>
                        <FaClipboardCheck size={20} />
                    </IconButton>
                </Tooltip>
            )}
            {(isFechamentoView || (!step && rowStep(row) === 7)) && (
                <Tooltip title="Fechamento" arrow placement="top">
                    <IconButton color="primary" LinkComponent={Link} to={`/producao-fechamento/${row.id}`}>
                        <FaCheck size={20} color="success.main" />
                    </IconButton>
                </Tooltip>
            )}
            {verifyPermission(componentName, 2) && !isTriagemView && !isFechamentoView && rowStep(row) !== 0 && rowStep(row) !== 7 && (
                <Tooltip title="Ver detalhes" arrow placement="top">
                    <IconButton color="primary" LinkComponent={Link} to={`/producao-detalhes/${row.id}`}>
                        <FaMagnifyingGlass size={20} />
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    );

    return (
        <ListingPage
            isLoading={isFetching}
            icon={FaIndustry}
            pageTitle={dinamicPageTitle}
            columns={columns}
            data={dataList}
            actions={renderActions}
            steps={renderStepComponent}
            filters={filters}
            filterValues={filterValues}
            onFilterChange={(field, value) => setFilterValues((prev) => ({ ...prev, [field]: value }))}
            getRowHref={(row) => {
                if (step === "triagem") return `/producao-triagem/${row.id}`;
                if (step === "fechamento") return `/producao-fechamento/${row.id}`;
                const current = Number(row.step);
                if (!step && current === 0) return `/producao-triagem/${row.id}`;
                if (!step && current === 7) return `/producao-fechamento/${row.id}`;
                return `/producao-detalhes/${row.id}`;
            }}
        />
    );
};